import type { ReactNode } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useWizard } from "@/contexts/WizardContext";
import { useToast } from "@/hooks/use-toast";
import { DocumentUploadSection } from "../common/DocumentUploadSection";
import { LockedBanner } from "../common/LockedBanner";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { InfoCallout } from "../common/InfoCallout";
import { SectionCard } from "../common/SectionCard";
import { TransitionMeasuresBlock } from "@/components/workflow/common/TransitionMeasuresBlock";
import { ProgressCard } from "../common/ProgressCard";
import { cn } from "@/lib/utils";

interface TransparencyCardProps {
  onCollapse: () => void;
}

function ChoiceCard({
  checked,
  disabled,
  label,
  name,
  id,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  name: string;
  id: string;
  onChange: () => void;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex min-h-[72px] flex-1 cursor-pointer items-center gap-3 rounded-2xl border p-4 transition-colors",
        checked ? "border-primary/20 bg-primary/5" : "border-slate-200 bg-white hover:border-slate-300",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <input
        id={id}
        type="radio"
        name={name}
        className="sr-only"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <span
        aria-hidden="true"
        className={cn(
          "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border bg-white",
          checked ? "border-primary" : "border-slate-500",
        )}
      >
        <span className={cn("h-2 w-2 rounded-full bg-primary", !checked && "opacity-0")} />
      </span>
      <span className="text-sm font-medium leading-5 text-slate-900">{label}</span>
    </label>
  );
}

export function TransparencyCard({ onCollapse }: TransparencyCardProps) {
  const { transparencyData, updateTransparencyData, transparencyCompletion, forceSave, saveStatus, isLocked, isReadOnly } = useWizard();
  const disabled = isLocked || isReadOnly;
  const { toast } = useToast();
  const handleSave = async () => {
    try {
      await forceSave();
      toast({
        title: "Saved successfully",
        description: "Transparency data has been saved.",
      });
    } catch (error) {
      toast({
        title: "Save failed",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title={"Transparency"}
        description={"Describe your financial reporting and stakeholder communication practices"}
        onClose={onCollapse}
        onSave={() => { void handleSave(); }}
        saveStatus={saveStatus}
        disabled={disabled}
        tag={"Governance"}
      />

      {disabled && <LockedBanner />}

      <div className="flex flex-col gap-6">
        {/* Reporting Frequency */}
        <SectionCard
          header={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col gap-1.5">
                <h3 className="text-xl font-semibold leading-7 text-slate-900">
                  {"Reporting Frequency"}
                  <span className="ml-1 text-red-500">*</span>
                </h3>
                <p className="text-sm leading-5 text-slate-500">
                  {"Do you prepare financial statements?"}
                </p>
              </div>
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <ChoiceCard
                checked={transparencyData.hasFinancialStatements === true}
                disabled={disabled}
                label={"Yes"}
                name="financial_statements"
                id="fin_yes"
                onChange={() => updateTransparencyData({ hasFinancialStatements: true })}
              />
              <ChoiceCard
                checked={transparencyData.hasFinancialStatements === false}
                disabled={disabled}
                label={"No"}
                name="financial_statements"
                id="fin_no"
                onChange={() => updateTransparencyData({ hasFinancialStatements: false })}
              />
            </div>

            {transparencyData.hasFinancialStatements === true && (
              <div className="flex flex-col gap-2 border-t border-slate-100 pt-4">
                <Label className="form-label">{"How often?"}</Label>
                <Select
                  value={transparencyData.financialStatementsFrequency || "annual"}
                  onValueChange={(value) => updateTransparencyData({ financialStatementsFrequency: value })}
                  disabled={disabled}
                >
                  <SelectTrigger className="form-select h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">{"Annual"}</SelectItem>
                    <SelectItem value="quarterly">{"Quarterly"}</SelectItem>
                    <SelectItem value="monthly">{"Monthly"}</SelectItem>
                    <SelectItem value="other">{"Other"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </SectionCard>

        {/* External Audit */}
        <SectionCard
          header={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col gap-1.5">
                <h3 className="text-xl font-semibold leading-7 text-slate-900">
                  {"External Audit / Review"}
                </h3>
                <p className="text-sm leading-5 text-slate-500">
                  {"Are your financial statements externally audited or reviewed?"}
                </p>
              </div>
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <ChoiceCard
                checked={transparencyData.hasExternalAudit === true}
                disabled={disabled}
                label={"Yes"}
                name="external_audit"
                id="audit_yes"
                onChange={() => updateTransparencyData({ hasExternalAudit: true })}
              />
              <ChoiceCard
                checked={transparencyData.hasExternalAudit === false}
                disabled={disabled}
                label={"No"}
                name="external_audit"
                id="audit_no"
                onChange={() => updateTransparencyData({ hasExternalAudit: false })}
              />
            </div>
            <InfoCallout
              label={"Note:"}
              text={"External audit increases credibility and transparency of financial reporting"}
            />
          </div>
        </SectionCard>

        {/* Stakeholder Communication */}
        <SectionCard
          header={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col gap-1.5">
                <h3 className="text-xl font-semibold leading-7 text-slate-900">
                  {"Stakeholder Communication"}
                  <span className="ml-1 text-red-500">*</span>
                </h3>
                <p className="text-sm leading-5 text-slate-500">
                  {"How do you communicate with stakeholders (customers, community, regulators)?"}
                </p>
              </div>
            </div>
          }
        >
          <Textarea
            placeholder={"e.g. Annual reports, community meetings, social media updates, stakeholder engagement sessions, regulatory filings, transparent pricing/billing, website information, etc."}
            className="form-input min-h-[100px] py-3"
            value={transparencyData.stakeholderCommunication || ""}
            onChange={(e) => updateTransparencyData({ stakeholderCommunication: e.target.value })}
            disabled={disabled}
          />
        </SectionCard>

        {/* Supporting Documents */}
        <SectionCard
          header={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col gap-1.5">
                <h3 className="text-xl font-semibold leading-7 text-slate-900">
                  {"Supporting Documents (Optional)"}
                </h3>
                <p className="text-sm leading-5 text-slate-500">
                  {"Add links or upload supporting evidence"}
                </p>
              </div>
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label className="form-label">{"Links to reports or documents"}</Label>
              <Textarea
                placeholder={"e.g. https://example.com/annual-report, https://example.com/sustainability-report, etc."}
                className="form-input min-h-[80px] py-3 text-sm"
                value={transparencyData.supportingDocumentsLinks || ""}
                onChange={(e) => updateTransparencyData({ supportingDocumentsLinks: e.target.value })}
                disabled={disabled}
              />
            </div>

            <div className="rounded-[24px] border-2 border-slate-200 bg-slate-50 p-4 sm:p-10">
              <DocumentUploadSection
                fieldIdentifier="governance-transparency"
                documents={transparencyData.supportingDocuments || []}
                onDocumentsChange={(docs) => updateTransparencyData({ supportingDocuments: docs })}
                currentTab="governance"
                label={"Financial Reports & Audit Statements"}
                description={"Upload financial reports and audit statements"}
                accept=".pdf,.xlsx,.xls,.csv"
                maxFiles={10}
                borderColor="border-slate-200"
                hideHeader
                className="bg-transparent p-0"
                emptyStateButtonClassName="rounded-[24px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
                documentsListClassName="space-y-3"
                itemClassName="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
                actionButtonClassName="rounded-3xl border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
                emptyStateContent={
                  <div className="flex flex-col items-center text-center">
                    <div className="pb-4">
                      <div className="rounded-full bg-slate-500/5 p-4">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 shrink-0 text-primary"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>
                      </div>
                    </div>
                    <p className="text-base font-bold leading-6 text-slate-800">Click to upload or drag and drop</p>
                    <p className="pt-1 text-sm leading-5 text-slate-500">{"Upload financial reports and audit statements"}</p>
                  </div>
                }
              />
            </div>
          </div>
        </SectionCard>

        <TransitionMeasuresBlock topicId="transparency" currentTab="governance" disabled={disabled} />

        <ProgressCard completion={transparencyCompletion} />
      </div>
    </div>
  );
}
